import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppStore } from '@/stores/app-store';

type VersionInfo = {
  version: string;
  builtAt?: string;
};

const DEFAULT_CHECK_INTERVAL_MS = 60_000;

export function useAppVersionChecker(intervalMs = DEFAULT_CHECK_INTERVAL_MS) {
  const enabled = import.meta.env.VITE_ENABLE_CHECK_NEW_VERSION === 'true';
  const savedVersion = useAppStore((s) => s.appVersion);
  const setAppVersion = useAppStore((s) => s.setAppVersion);
  const [hasUpdate, setHasUpdate] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const latestVersionRef = useRef<string | null>(null);

  const reload = useCallback(() => {
    const next = latestVersionRef.current;
    if (next) setAppVersion(next);
    window.location.reload();
  }, [setAppVersion]);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    const fetchVersion = async (): Promise<VersionInfo | null> => {
      try {
        const res = await fetch('/version.json', {
          cache: 'no-store',
        });
        if (!res.ok) return null;
        const data = (await res.json()) as VersionInfo;
        return data;
      } catch {
        return null;
      }
    };

    const scheduleNext = () => {
      if (!cancelled) {
        timerRef.current = setTimeout(pollVersion, intervalMs);
      }
    };

    // ポーリング処理（前回完了を待ってから次を実行）
    const pollVersion = async () => {
      if (cancelled) return;
      try {
        const data = await fetchVersion();
        if (data?.version && !cancelled) {
          latestVersionRef.current = data.version;
          if (savedVersion && data.version !== savedVersion) {
            setHasUpdate(true);
          }
        }
      } finally {
        scheduleNext();
      }
    };

    // 初回実行（バージョン保存処理）
    // いま読み込んでいるバンドルはページロード時点のデプロイ内容なので、取得したバージョンを
    // そのまま基準として保存する。前回セッションの保存値と比較すると、デプロイ後の初回アクセスで
    // 「最新版を読み込み済みなのに更新あり」と誤判定して不要なリロードを促してしまう。
    (async () => {
      try {
        const data = await fetchVersion();
        if (data?.version && !cancelled) {
          latestVersionRef.current = data.version;
          if (data.version !== savedVersion) setAppVersion(data.version);
          setHasUpdate(false);
        }
      } finally {
        scheduleNext();
      }
    })();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [intervalMs, savedVersion, setAppVersion]);

  return { enabled, hasUpdate, reload };
}
